import React, {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useMsal } from '@azure/msal-react';
import {
  AccountInfo,
  BrowserAuthError,
  InteractionStatus,
} from '@azure/msal-browser';
import { apiRequest } from './authConfig';
import {
  type AuthenticatedRequest,
  withAuthRetry,
} from './auth/authenticated-request';

// User types
type UserType = {
  userID: string;
  name: string;
  displayName?: string;
  accessToken: string;
  refreshAccessToken: () => Promise<string>;
} | null;

const REDIRECT_ATTEMPTED_KEY = 'silentRenewalRedirectAttempted';

type SessionStatus = 'acquiring' | 'ready' | 'sign-in-required';

type SessionType = {
  status: SessionStatus;
  signInAgain: () => Promise<void>;
};

// Create a context for the user with a default value
const UserContext = createContext<UserType>(null);

const SessionContext = createContext<SessionType>({
  signInAgain: () => Promise.resolve(),
  status: 'acquiring',
});

// Create a provider component
const UserProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { accounts, inProgress, instance } = useMsal();
  const [user, setUser] = useState<UserType>(null);
  const [status, setStatus] = useState<SessionStatus>('acquiring');

  const acquireTokenSilently = useCallback(
    async (forceRefresh = false) => {
      instance.setActiveAccount(accounts[0]);
      await instance.initialize();
      const response = await instance.acquireTokenSilent({
        ...apiRequest,
        account: accounts[0],
        forceRefresh,
      });
      return response.accessToken;
    },
    [accounts, instance],
  );

  // A request's 401 retry lands here too, so a dead-in-session token (not just a
  // failed initial load) must also flip the session into recovery.
  const refreshAccessToken = useCallback(async () => {
    try {
      const accessToken = await acquireTokenSilently(true);
      setUser(current => (current ? { ...current, accessToken } : current));
      return accessToken;
    } catch (error: unknown) {
      console.error('Refreshing the access token failed', error);
      setStatus('sign-in-required');
      throw error;
    }
  }, [acquireTokenSilently]);

  const signIn = useCallback(
    (accessToken: string, account: AccountInfo) => {
      setUser({
        accessToken,
        displayName: account.name,
        name: account.username,
        refreshAccessToken,
        userID: account.localAccountId,
      });
      setStatus('ready');
    },
    [refreshAccessToken],
  );

  // A popup opened outside a click is blocked, so recovery is a button, not a retry here.
  const signInAgain = useCallback(async () => {
    setStatus('acquiring');
    try {
      const response = await instance.acquireTokenPopup({
        ...apiRequest,
        account: accounts[0],
      });
      signIn(response.accessToken, response.account ?? accounts[0]);
    } catch (error: unknown) {
      // Dismissing the popup isn't a failure worth logging.
      if (
        !(error instanceof BrowserAuthError) ||
        error.errorCode !== 'user_cancelled'
      ) {
        console.error('Interactive token acquisition failed', error);
      }
      setStatus('sign-in-required');
    }
  }, [accounts, instance, signIn]);

  useEffect(() => {
    if (accounts.length === 0 || inProgress !== InteractionStatus.None) {
      return;
    }

    // msal-react rebuilds `accounts` (new array identity) whenever the underlying
    // account list changes, which can re-run this effect before the previous
    // acquisition settles; ignore a stale run's result rather than let it clobber
    // a newer one.
    let stale = false;
    const account = accounts[0];
    setStatus('acquiring');
    acquireTokenSilently()
      .then(accessToken => {
        if (!stale) {
          sessionStorage.removeItem(REDIRECT_ATTEMPTED_KEY);
          signIn(accessToken, account);
        }
      })
      .catch((error: unknown) => {
        if (stale) {
          return;
        }
        console.error('Silent token acquisition failed', error);
        // Firefox blocks the cookie MSAL's hidden-iframe renewal needs; a
        // top-level redirect still carries it, so the Microsoft session renews
        // without a prompt. Nothing is on the page yet, so there's no state to
        // lose. Only once per tab, so a failing redirect can't loop.
        if (sessionStorage.getItem(REDIRECT_ATTEMPTED_KEY)) {
          sessionStorage.removeItem(REDIRECT_ATTEMPTED_KEY);
          setStatus('sign-in-required');
          return;
        }
        sessionStorage.setItem(REDIRECT_ATTEMPTED_KEY, 'true');
        instance
          .acquireTokenRedirect({ ...apiRequest, account })
          .catch((redirectError: unknown) => {
            console.error('Redirect token acquisition failed', redirectError);
            setStatus('sign-in-required');
          });
      });

    return () => {
      stale = true;
    };
  }, [accounts, acquireTokenSilently, inProgress, instance, signIn]);

  return (
    <SessionContext.Provider value={{ signInAgain, status }}>
      <UserContext.Provider value={user}>{children}</UserContext.Provider>
    </SessionContext.Provider>
  );
};

const useSession = (): SessionType => useContext(SessionContext);

const useAuthenticatedRequest = (accessToken?: string) => {
  const user = useContext(UserContext);

  return useCallback(
    <T,>(request: AuthenticatedRequest<T>) =>
      withAuthRetry(
        request,
        accessToken || user?.accessToken || '',
        user?.refreshAccessToken,
      ),
    [accessToken, user],
  );
};

export { UserContext, UserProvider, useAuthenticatedRequest, useSession };
export type { SessionStatus, UserType };
